import { Routes } from '@angular/router';
import { HomeComponent } from './home/home.component';
import ShellComponent from './shell/shell.component';
import { AuthGuard } from './shared/guards/auth.guard';
import { RoleGuard } from './shared/guards/role.guard';
import { RouteConstants } from './shared/constants/route.constant';
import { unsavedChangesGuard } from './shared/guards/unsaved-changes.guard';
import { ConsentGuard } from './shared/guards/consent.guard';
import { contactsAccessGuard } from './shared/guards/contacts-access.guard';

export const routes: Routes = [
  {
    path: RouteConstants.STRIPE_PRICING_ROUTE,
    loadComponent: () => import('./stripe-pricing/stripe-pricing.component'),
    canActivate: [RoleGuard],
  },
  {
    path: RouteConstants.CONSENT_ROUTE,
    loadComponent: () => import('./consent/consent.component'),
    canActivate: [AuthGuard],
  },
  // Unguarded: third-party attribution has to be readable without signing in.
  {
    path: RouteConstants.LICENSES_ROUTE,
    loadComponent: () => import('./licenses/licenses.component'),
  },
  {
    path: '',
    component: HomeComponent,
  },
  {
    path: '',
    component: ShellComponent,
    canActivate: [AuthGuard, RoleGuard, ConsentGuard],
    children: [
      {
        path: RouteConstants.DASHBOARD_ROUTE,
        loadComponent: () => import('./dashboard/dashboard.page'),
      },
      {
        path: RouteConstants.GROUPS_ROUTE,
        children: [
          {
            path: ':uid',
            loadComponent: () => import('./groups/edit-group/edit-group.component'),
            canDeactivate: [unsavedChangesGuard],
          },
          {
            path: '',
            loadComponent: () => import('./groups/groups.component'),
          },
        ],
      },
      {
        path: RouteConstants.ADMINS_ROUTE,
        loadComponent: () => import('./admins/admins.page'),
      },
      {
        path: RouteConstants.ORGANIZATION_SETTINGS_ROUTE,
        children: [
          {
            path: RouteConstants.ORGANIZATION_CLIENT_CATEGORIES_ROUTE,
            loadComponent: () =>
              import('./organizations/single-organization/organization-settings/organization-client-categories/organization-client-categories.component'),
          },
          {
            path: RouteConstants.ORGANIZATION_CLIENT_CONTENT_VISIBILITY_ROUTE,
            loadComponent: () =>
              import('./organizations/single-organization/organization-settings/organization-client-content-visibility/organization-client-content-visibility.component'),
          },
          {
            path: '',
            loadComponent: () => import('./organization-settings/organization-settings.page'),
          },
        ],
      },
      {
        path: RouteConstants.ORGANIZATIONS_ROUTE,
        children: [
          {
            path: ':uid',
            loadComponent: () => import('./organizations/single-organization/single-organization.component'),
            children: [
              {
                path: RouteConstants.ORGANIZATION_TEAMS_ROUTE,
                loadComponent: () =>
                  import('./organizations/single-organization/organization-teams/organization-teams.component'),
              },
              {
                path: RouteConstants.ORGANIZATION_ADMINS_ROUTE,
                loadComponent: () =>
                  import('./organizations/single-organization/organization-admins/organization-admins.component'),
              },
              {
                path: RouteConstants.ORGANIZATION_CLIENTS_ROUTE,
                loadComponent: () =>
                  import('./organizations/single-organization/organization-clients/organization-clients.component'),
              },
              {
                path: RouteConstants.ORGANIZATION_COACHES_ROUTE,
                loadComponent: () =>
                  import('./organizations/single-organization/organization-coaches/organization-coaches.component'),
              },
              {
                path: RouteConstants.ORGANIZATION_PROFILE_ROUTE,
                loadComponent: () =>
                  import('./organizations/single-organization/organization-profile/organization-profile.component'),
              },
              {
                path: RouteConstants.SETTINGS_ROUTE,
                children: [
                  {
                    path: RouteConstants.ORGANIZATION_CLIENT_CATEGORIES_ROUTE,
                    loadComponent: () =>
                      import('./organizations/single-organization/organization-settings/organization-client-categories/organization-client-categories.component'),
                  },
                  {
                    path: RouteConstants.ORGANIZATION_CLIENT_CONTENT_VISIBILITY_ROUTE,
                    loadComponent: () =>
                      import('./organizations/single-organization/organization-settings/organization-client-content-visibility/organization-client-content-visibility.component'),
                  },
                  {
                    path: '',
                    loadComponent: () =>
                      import('./organizations/single-organization/organization-settings/organization-settings.component'),
                  },
                ],
              },
              {
                path: '',
                redirectTo: RouteConstants.ORGANIZATION_PROFILE_ROUTE,
                pathMatch: 'prefix',
              },
            ],
          },
          {
            path: '',
            loadComponent: () => import('./organizations/organizations.component'),
          },
        ],
      },
      {
        path: RouteConstants.CHAT_ROUTE,
        loadComponent: () => import('./chat/chat.component'),
      },
      {
        path: RouteConstants.CALENDAR_ROUTE,
        loadComponent: () => import('./coach-calendar/coach-calendar.component'),
        canActivate: [contactsAccessGuard],
      },
      {
        path: RouteConstants.CLIENTS_ROUTE,
        children: [
          {
            path: ':uid',
            loadChildren: () =>
              import('./clients/single-client/single-client/single-client.routes').then((r) => r.routes),
          },
          {
            path: '',
            loadComponent: () => import('./clients/clients.component'),
          },
        ],
      },

      {
        path: RouteConstants.SETTINGS_ROUTE,
        children: [
          {
            path: '',
            loadComponent: () => import('./settings/settings.component'),
          },
          {
            path: RouteConstants.SETTINGS_WORKOUT_EXERCISE_ROUTE,
            loadChildren: () =>
              import('./settings/workout-exercise-settings/workout-exercise-settings.routes').then((r) => r.routes),
          },
          {
            path: RouteConstants.SETTINGS_MEALS_ROUTE,
            loadChildren: () => import('./settings/meals-settings/meals-settings.routes').then((r) => r.routes),
          },
          {
            path: RouteConstants.SETTINGS_HABIT_CATEGORIES_ROUTE,
            loadComponent: () => import('./settings/habits-settings/habits-settings.component'),
          },
          // Middle-breadcrumb fallback: clicking "Habits" in
          // Settings / Habits / Habit Categories navigates to
          // /settings/habits, which has no route of its own. Without
          // this redirect Angular's top-level ** fires and dumps the
          // user on /dashboard. Redirect back to the only child so
          // the click is a no-op (matches the Meals pattern).
          {
            path: 'habits',
            pathMatch: 'full',
            redirectTo: RouteConstants.SETTINGS_HABIT_CATEGORIES_ROUTE,
          },
          {
            path: RouteConstants.SETTINGS_REFLECTION_CATEGORIES_ROUTE,
            loadComponent: () =>
              import('./settings/reflection-categories-settings/reflection-categories-settings.component'),
          },
          // Same middle-breadcrumb fallback as Habits — keeps clicks on
          // "Reflections" in the breadcrumb on the same page instead of
          // bouncing to /dashboard.
          {
            path: 'reflections',
            pathMatch: 'full',
            redirectTo: RouteConstants.SETTINGS_REFLECTION_CATEGORIES_ROUTE,
          },
          {
            path: RouteConstants.SETTINGS_QUIZ_CATEGORIES_ROUTE,
            loadComponent: () => import('./settings/quiz-categories-settings/quiz-categories-settings.component'),
          },
          // Same middle-breadcrumb fallback as Reflections.
          {
            path: 'quizzes',
            pathMatch: 'full',
            redirectTo: RouteConstants.SETTINGS_QUIZ_CATEGORIES_ROUTE,
          },
          {
            path: RouteConstants.SETTINGS_GOALS_METRICS_ROUTE,
            loadChildren: () =>
              import('./settings/goals-metrics-settings/goals-metrics-settings.routes').then((r) => r.routes),
          },
          {
            path: RouteConstants.SETTINGS_METABOLIC_ACTIVITIES_ROUTE,
            loadComponent: () =>
              import('./settings/metabolic-activities-settings/metabolic-activities-settings.component'),
          },
        ],
      },

      {
        path: RouteConstants.CONFIGURE_ROUTE,
        children: [
          {
            path: '',
            loadComponent: () => import('./settings/translation/translation.component'),
            canActivate: [RoleGuard],
          },
          {
            path: RouteConstants.TRANSLATION_INGREDIENTS_ROUTE,
            loadComponent: () =>
              import('./settings/translation/translation-ingredients/translation-ingredients.component'),
          },

          {
            path: RouteConstants.TRANSLATION_MEALS_ROUTE,
            loadComponent: () => import('./settings/translation/translation-meals/translation-meals.component'),
          },
        ],
      },

      {
        path: RouteConstants.COACHES_ROUTE,
        children: [
          {
            path: '',
            loadComponent: () => import('./coaches/coaches.component'),
          },
        ],
      },
      {
        path: RouteConstants.TEAMS_ROUTE,
        children: [
          {
            path: '',
            loadComponent: () => import('./teams/teams.component'),
          },
        ],
      },

      {
        path: RouteConstants.WORKOUTS_ROUTE,

        children: [
          {
            path: RouteConstants.WORKOUTS_ROUTE,
            children: [
              {
                path: '',
                loadComponent: () => import('./workouts/workouts/workouts.component'),
              },
              {
                path: ':uid',
                loadComponent: () => import('./workouts/workouts/single-workout/single-workout.component'),
              },
            ],
          },

          {
            path: RouteConstants.WORKOUT_PROGRAMS_ROUTE,
            children: [
              {
                path: '',
                loadComponent: () => import('./workouts/workout-programs/workout-programs.component'),
              },
              {
                path: ':uid',
                loadComponent: () =>
                  import('./workouts/workout-programs/single-workout-program/single-workout-program.component'),
              },
            ],
          },

          {
            path: RouteConstants.EXERCISES_ROUTE,
            children: [
              {
                path: '',
                loadComponent: () => import('./workouts/exercises/exercises.component'),
              },
            ],
          },

          {
            path: RouteConstants.GENERAL_WORKOUTS_ROUTE,
            children: [
              {
                path: '',
                loadComponent: () => import('./workouts/general-workouts/general-workouts.component'),
                canActivate: [RoleGuard],
              },
            ],
          },

          {
            path: RouteConstants.DAILY_WORKOUTS_ROUTE,
            children: [
              {
                path: '',
                loadComponent: () => import('./workouts/daily-workouts/daily-workouts.component'),
              },

              {
                path: ':uid',
                loadComponent: () =>
                  import('./workouts/daily-workouts/single-daily-workouts/single-daily-workouts.component'),
              },
            ],
          },

          {
            path: '**',
            pathMatch: 'full',
            redirectTo: RouteConstants.WORKOUTS_ROUTE,
          },
        ],
      },

      {
        path: RouteConstants.WORKOUTS_ADD_ROUTE,
        children: [
          {
            path: ':uid',
            loadComponent: () => import('./workouts/workouts/single-workout/single-workout.component'),
          },
        ],
      },

      {
        path: RouteConstants.MEALS_ROUTE,

        children: [
          {
            path: RouteConstants.MEALS_ROUTE,
            children: [
              {
                path: '',
                loadComponent: () => import('./meals/meals/meals.component'),
              },
            ],
          },

          {
            path: RouteConstants.INGREDIENTS_ROUTE,
            loadComponent: () => import('./meals/ingredients/ingredients.component'),
          },

          {
            path: RouteConstants.MEAL_PLAN_ROUTE,
            children: [
              {
                path: '',
                loadComponent: () => import('./meals/meal-plan/meal-plan.component'),
              },
              {
                path: ':uid',
                loadComponent: () => import('./meals/meal-plan/single-meal-plan/single-meal-plan.component'),
              },
            ],
          },

          {
            path: RouteConstants.DAILY_MEALS_ROUTE,

            children: [
              {
                path: '',
                loadComponent: () => import('./meals/daily-meals/daily-meals.component'),
              },

              {
                path: ':uid',
                loadComponent: () => import('./meals/daily-meals/single-daily-meals/single-daily-meals.component'),
              },
            ],
          },
          {
            path: '**',
            pathMatch: 'full',
            redirectTo: RouteConstants.MEALS_ROUTE,
          },
        ],
      },

      {
        path: RouteConstants.HABITS_ROUTE,
        children: [
          {
            path: RouteConstants.HABITS_ROUTE,
            children: [
              {
                path: '',
                loadComponent: () => import('./habits/habits.component'),
              },
            ],
          },

          {
            path: RouteConstants.SCHEDULER_ROUTE,
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./habits/habits-scheduler/habits-scheduler.component').then(
                    (m) => m.HabitsSchedulerComponent,
                  ),
              },
            ],
          },
          {
            path: '**',
            pathMatch: 'full',
            redirectTo: RouteConstants.HABITS_ROUTE,
          },
        ],
      },

      {
        path: RouteConstants.REFLECTIONS_ROUTE,
        children: [
          {
            path: RouteConstants.REFLECTIONS_ROUTE,
            children: [
              {
                path: `${RouteConstants.EDIT_REFLECTION_ROUTE}/:uid`,
                loadComponent: () =>
                  import('./reflections/reflections/create-edit-reflection/create-edit-reflection.component'),
                canDeactivate: [unsavedChangesGuard],
              },
              {
                path: `${RouteConstants.VIEW_REFLECTION_ROUTE}/:uid`,
                loadComponent: () =>
                  import('./reflections/reflections/create-edit-reflection/create-edit-reflection.component'),
              },
              {
                path: RouteConstants.ADD_REFLECTION_ROUTE,
                loadComponent: () =>
                  import('./reflections/reflections/create-edit-reflection/create-edit-reflection.component'),
                canDeactivate: [unsavedChangesGuard],
              },
              {
                path: '',
                loadComponent: () => import('./reflections/reflections/reflections.component'),
              },
            ],
          },
          {
            path: RouteConstants.QUESTIONS_ROUTE,
            loadComponent: () => import('./reflections/questions/questions.component'),
          },

          {
            path: RouteConstants.SCHEDULER_ROUTE,
            loadComponent: () =>
              import('./reflections/reflections-scheduler/reflections-scheduler.component').then(
                (m) => m.ReflectionsSchedulerComponent,
              ),
          },

          {
            path: '**',
            pathMatch: 'full',
            redirectTo: RouteConstants.REFLECTIONS_ROUTE,
          },
        ],
      },
      {
        path: `${RouteConstants.BUNDLES_ROUTE}/${RouteConstants.BUNDLES_ROUTE}/:uid`,
        loadComponent: () => import('./bundles-shell/bundles/create-edit-bundle/create-edit-bundle.page'),
        canDeactivate: [unsavedChangesGuard],
      },
      {
        path: `${RouteConstants.BUNDLES_ROUTE}/${RouteConstants.BUNDLES_ROUTE}/${RouteConstants.VIEW_BUNDLE_ROUTE}`,
        loadComponent: () => import('./bundles-shell/bundles/create-edit-bundle/create-edit-bundle.page'),
      },
      {
        path: RouteConstants.BUNDLES_ROUTE,
        loadComponent: () => import('./bundles-shell/bundles-shell.component'),
        children: [
          {
            path: RouteConstants.BUNDLES_ROUTE,
            children: [
              /* {
                path: `${RouteConstants.EDIT_BUNDLE_ROUTE}/:uid`,
                loadComponent: () =>
                  import(
                    './bundles-shell/bundles/create-edit-bundle/create-edit-bundle.page'
                  ),
                canDeactivate: [unsavedChangesGuard],
              },
              {
                path: `${RouteConstants.VIEW_BUNDLE_ROUTE}/:uid`,
                loadComponent: () =>
                  import(
                    './bundles-shell/bundles/create-edit-bundle/create-edit-bundle.page'
                  ),
              },
              {
                path: RouteConstants.CREATE_BUNDLE_ROUTE,
                loadComponent: () =>
                  import(
                    './bundles-shell/bundles/create-edit-bundle/create-edit-bundle.page'
                  ),
                canDeactivate: [unsavedChangesGuard],
              }, */
              {
                path: '',
                loadComponent: () => import('./bundles-shell/bundles/bundles.page'),
              },
            ],
          },
          {
            path: RouteConstants.ORGANIZATIONS_ROUTE,
            loadComponent: () => import('./bundles-shell/organizations/organizations.component'),
          },
          {
            path: '**',
            pathMatch: 'full',
            redirectTo: RouteConstants.BUNDLES_ROUTE,
          },
        ],
      },
      {
        path: RouteConstants.QUIZ_ROUTE,

        children: [
          {
            path: RouteConstants.QUIZZES_ROUTE,
            children: [
              {
                path: `${RouteConstants.EDIT_QUIZ_ROUTE}/:uid`,
                loadComponent: () => import('./quiz/quizzes/create-quiz/create-quiz.component'),
                canDeactivate: [unsavedChangesGuard],
              },
              {
                path: `${RouteConstants.VIEW_QUIZ_ROUTE}/:uid`,
                loadComponent: () => import('./quiz/quizzes/create-quiz/create-quiz.component'),
                canDeactivate: [unsavedChangesGuard],
              },
              {
                path: RouteConstants.ADD_QUIZ_ROUTE,
                loadComponent: () => import('./quiz/quizzes/create-quiz/create-quiz.component'),
                canDeactivate: [unsavedChangesGuard],
              },
              {
                path: '',
                loadComponent: () => import('./quiz/quizzes/quizzes.component'),
              },
            ],
          },
          {
            path: RouteConstants.QUESTIONS_ROUTE,
            loadComponent: () => import('./quiz/questions/questions.component'),
          },

          {
            path: RouteConstants.SCHEDULER_ROUTE,
            loadComponent: () =>
              import('./quiz/quiz-scheduler/quiz-scheduler.component').then((m) => m.QuizSchedulerComponent),
          },
          {
            path: RouteConstants.QUIZ_PLANS_ROUTE,
            children: [
              {
                path: '',
                loadComponent: () => import('./quiz/quiz-plans/quiz-plans.component'),
              },
              {
                path: ':uid',
                loadComponent: () => import('./quiz/quiz-plans/single-quiz-plan/single-quiz-plan.component'),
              },
            ],
          },
          {
            path: '**',
            pathMatch: 'full',
            redirectTo: RouteConstants.QUIZZES_ROUTE,
          },
        ],
      },
      {
        path: RouteConstants.ASSESSMENTS_ROUTE,
        children: [
          {
            path: RouteConstants.ASSESSMENTS_ROUTE,
            children: [
              {
                path: `${RouteConstants.EDIT_ASSESSMENT_ROUTE}/:uid`,
                loadComponent: () => import('./assessments/create-edit-assessment/create-edit-assessment.component'),
                canDeactivate: [unsavedChangesGuard],
              },
              {
                path: `${RouteConstants.VIEW_ASSESSMENT_ROUTE}/:uid`,
                loadComponent: () => import('./assessments/create-edit-assessment/create-edit-assessment.component'),
                canDeactivate: [unsavedChangesGuard],
              },
              {
                path: RouteConstants.ADD_ASSESSMENT_ROUTE,
                loadComponent: () => import('./assessments/create-edit-assessment/create-edit-assessment.component'),
                canDeactivate: [unsavedChangesGuard],
              },
              {
                path: '',
                loadComponent: () => import('./assessments/assessments/assessments.component'),
              },
            ],
          },
          {
            path: '**',
            pathMatch: 'full',
            redirectTo: RouteConstants.ASSESSMENTS_ROUTE,
          },
        ],
      },
      {
        path: RouteConstants.ANNOUNCEMENTS_ROUTE,
        loadChildren: () => import('./announcements/announcements.routes').then((r) => r.routes),
      },
      {
        path: RouteConstants.PROFILE_ROUTE,
        loadComponent: () => import('./profile/profile.component'),
      },

      {
        path: '**',
        redirectTo: '/dashboard',
        pathMatch: 'full',
      },
    ],
  },
];
